import { createElement, forwardRef, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Input, InputChangeEvent, InputCommitEvent } from '../../Input';
import { setRef } from '../../../util/ref-util';
import { NumericStepperInputProps } from '.';
import cx from 'classnames';
import cn from '../NumericStepper.css.json';

export const NumericStepperInput = forwardRef<HTMLInputElement, NumericStepperInputProps>(
  ({ editMode = 'select', readOnly = false, onStateChange, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    // auto-width
    useLayoutEffect(() => {
      const input = inputRef.current;
      if (input && input.parentElement) {
        if (props.style?.width) {
          input.parentElement.style.width = '100%';
          return;
        }
        const style = window.getComputedStyle(input);
        const fontFamily = style.fontFamily;
        const fontSize = style.fontSize;
        const width = calculateInputWidth(fontSize, fontFamily, props.maxLength!);
        const inputWidth =
          width +
          parseInt(style.paddingLeft) +
          20 + // The padding-right is 8px when no status icon shown, otherwise it is 20px, and the larger one is reserved.
          parseInt(style.marginLeft) +
          parseInt(style.marginRight) +
          parseInt(style.borderLeftWidth) +
          parseInt(style.borderRightWidth) +
          2 + // to avoid the blinking caret found, make the width widen 2 pixels.
          1; // status icon take 1px more space which overlaps with input content.

        input.parentElement.style.width = `${inputWidth}px`;
        input.parentElement.style.minWidth = `${inputWidth}px`;
      }
    }, [props.maxLength, props.style?.width, editMode, readOnly]);

    // this flag to prevent 'select' event handler from being called recusively
    const selectHandlerHasBeenInvokedRef = useRef(false);
    // In 'select' edit mode, doesn't highlight whole text when focus in
    const handleInputSelect = useCallback(() => {
      if (selectHandlerHasBeenInvokedRef.current) {
        selectHandlerHasBeenInvokedRef.current = false;
        return;
      }
      const input = inputRef.current;
      if (input) {
        selectHandlerHasBeenInvokedRef.current = true;
        input.selectionStart = editMode === 'select' ? input.selectionEnd : 0;
      }
    }, [editMode]);

    useEffect(() => {
      const input = inputRef.current;

      input?.addEventListener('select', handleInputSelect);

      return () => {
        input?.removeEventListener('select', handleInputSelect);
      };
    }, [handleInputSelect]);

    return (
      <Input
        {...props}
        className={cx(!readOnly && cn.input, props.status && cn.hasStatus)}
        editable={props.disabled || (!readOnly && editMode === 'combo')}
        onChange={(e: InputChangeEvent) =>
          !props.disabled && onStateChange?.({ value: e.value, state: 'change' })
        }
        onCommit={(e: InputCommitEvent) =>
          !props.disabled &&
          onStateChange?.({
            value: e.value,
            state: e.reason === 'suggestion' || e.reason === undefined ? 'blur' : e.reason,
          })
        }
        onFocus={() => !props.disabled && onStateChange?.({ value: props.value, state: 'focus' })}
        ref={(element) => {
          inputRef.current = element;
          setRef(ref, element);
        }}
        role="spinbutton"
        type="number"
        variant={!readOnly && editMode === 'select' ? 'composite' : 'standard'}
      />
    );
  }
);

function calculateInputWidth(fontSize: string, fontFamily: string, length: number) {
  const span = document.createElement('span');
  let width = span.offsetWidth;
  span.style.visibility = 'hidden';
  span.style.fontSize = fontSize;
  span.style.fontFamily = fontFamily;
  span.style.display = 'inline-block';
  document.body.appendChild(span);

  const text = new Array(length).fill('0').join('');
  if (typeof span.textContent !== 'undefined') {
    span.textContent = text;
  } else {
    span.innerText = text;
  }
  width = parseFloat(window.getComputedStyle(span).width) - width;
  document.body.removeChild(span);
  return width;
}
